import React, { useState, useEffect } from 'react';
import './ManageRoles.css';
import LoadingState from './LoadingState';
import ConfirmModal from './ConfirmModal';

const AVAILABLE_ROLES = [
  'Owner',
  'General Manager',
  'Fit Specialist',
  'Assistant Sales Manager',
  'Sales Manager',
  'Business Development Center',
  'Finance & Insurance Manager',
  'Business Manager',
  'Service Manager',
  'Parts Manager',
  'Apparel & Licensing Manager',
  'Dashboard'
];

// Dealership-scoped management permissions. Only the General Manager role can be
// granted these, and only within the dealership(s) the GM works at. Configuring
// roles and setting targets remain owner/site-manager/admin only.
const GM_MANAGEMENT_FEATURES = [
  { id: 'manage-users', label: 'Manage Users', hint: 'Add and deactivate users in their dealership' },
  { id: 'edit-dropdowns', label: 'Edit Dropdowns', hint: 'Edit dropdown lists (lenders, fit specialists, etc.)' }
];

// Features that can be assigned to roles (owner-only features are excluded)
const FEATURES = [
  { id: 'overview', label: 'Overview' },
  { id: 'sales-activity', label: 'F&I Input' },
  { id: 'daily-logger', label: '10:4 Sales Process' },
  { id: 'parts-entry', label: 'Parts & Accessories' },
  { id: 'service-entry', label: 'Service Entry' },
  { id: 'apparel-entry', label: 'Apparel & Licensing' },
  { id: 'inventory-entry', label: 'Inventory' },
  { id: 'nps-input', label: 'NPS Input' },
  { id: 'sales-performance', label: 'Sales Performance' },
  { id: 'sales-scoreboard', label: 'Sales Scoreboard' },
  { id: 'sales-leader-scoreboard', label: 'Sales Leader Scoreboard' },
  { id: 'fixed-ops-performance', label: 'Fixed Operations Performance' },
  { id: 'fixedops-scoreboard', label: 'Fixed Ops Scoreboard' },
  { id: 'fi-scoreboard', label: 'F&I Scoreboard' },
  { id: 'profit-summary', label: 'Performance Metrics' }
];

// Beta features — only appear in this list when the admin has enabled them for the dealership
const BETA_FEATURES = [
  { id: 'insights', label: 'Insights', betaKey: 'insights' },
  { id: 'visual-sales-scoreboard', label: 'Visual Sales Dashboard', betaKey: 'visualSalesDashboard' },
];

// Owner-only features (always available to Owner, not configurable for other roles)
const OWNER_ONLY_FEATURES = [
  { id: 'dealerships', label: 'Dealerships' },
  { id: 'user-management', label: 'User Management' }
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Features that default to OFF for most roles — an admin can still flip them on
// per role in the grid below, this just controls the out-of-the-box state.
const DEFAULT_OFF_FEATURES = ['fi-scoreboard'];

// Define default feature access per role
const getDefaultFeaturesForRole = (role) => {
  const allFeatures = FEATURES.map(f => f.id);

  switch (role) {
    case 'Owner':
    case 'General Manager':
      return allFeatures;
    case 'Service Manager':
      return ['overview', 'service-entry', 'fixed-ops-performance', 'fixedops-scoreboard'];
    case 'Parts Manager':
      return ['overview', 'parts-entry', 'fixed-ops-performance', 'fixedops-scoreboard'];
    case 'Apparel & Licensing Manager':
      return ['overview', 'apparel-entry', 'fixed-ops-performance', 'fixedops-scoreboard'];
    case 'Dashboard':
      return ['overview', 'sales-scoreboard', 'fixedops-scoreboard'];
    default:
      // Everyone else (Fit Specialist, Sales/Assistant Sales Manager, BDC, F&I
      // Manager, Business Manager, and any custom role) starts without the
      // default-off features; Owner/GM can enable them per role above.
      return allFeatures.filter(f => !DEFAULT_OFF_FEATURES.includes(f));
  }
};

export default function ManageRoles({ user, managedOwner, dealershipBetaFeatures = {}, onClose }) {
  const [rolePermissions, setRolePermissions] = useState({});
  const [inactiveRoles, setInactiveRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customRoleInput, setCustomRoleInput] = useState('');

  const customRoles = Object.keys(rolePermissions).filter(r => !AVAILABLE_ROLES.includes(r));

  const activeFeatures = [
    ...FEATURES,
    ...BETA_FEATURES.filter(f => dealershipBetaFeatures[f.betaKey])
  ];

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmStyle: 'danger'
  });

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  // Fetch global role permissions from backend
  useEffect(() => {
    const fetchRolePermissions = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const url = managedOwner
          ? `${API_URL}/api/dealerships/role-permissions?ownerId=${encodeURIComponent(managedOwner.id)}`
          : `${API_URL}/api/dealerships/role-permissions`;
        const response = await fetch(url, { credentials: 'include', headers });

        if (!response.ok) {
          throw new Error('Failed to fetch role permissions');
        }

        const data = await response.json();
        const permissions = data.rolePermissions;

        console.log('[MANAGE ROLES] Fetched global permissions:', permissions);

        // If null/undefined (never configured), initialize with defaults
        // If {} (configured but empty), use it as-is
        if (permissions === null || permissions === undefined) {
          const defaults = {};
          AVAILABLE_ROLES.forEach(role => {
            defaults[role] = getDefaultFeaturesForRole(role);
          });
          setRolePermissions(defaults);
        } else {
          // Permissions exist (could be {} or populated)
          // Ensure all default roles have an entry, then preserve any custom roles
          const fullPermissions = {};
          AVAILABLE_ROLES.forEach(role => {
            fullPermissions[role] = permissions[role] || getDefaultFeaturesForRole(role);
          });
          Object.keys(permissions).forEach(role => {
            if (!AVAILABLE_ROLES.includes(role)) {
              fullPermissions[role] = permissions[role] || [];
            }
          });
          setRolePermissions(fullPermissions);
        }
      } catch (err) {
        console.error('Error fetching role permissions:', err);
        const defaults = {};
        AVAILABLE_ROLES.forEach(role => {
          defaults[role] = getDefaultFeaturesForRole(role);
        });
        setRolePermissions(defaults);
      } finally {
        setLoading(false);
      }
    };

    fetchRolePermissions();
  }, []);

  const handleToggleFeature = (role, featureId) => {
    // Prevent modifying owner-only features for non-owner roles
    const ownerOnlyIds = OWNER_ONLY_FEATURES.map(f => f.id);
    if (ownerOnlyIds.includes(featureId)) {
      setAlertModal({
        isOpen: true,
        title: 'Reserved Feature',
        message: 'This feature is reserved for Owner role only.'
      });
      return;
    }

    setRolePermissions(prev => ({
      ...prev,
      [role]: prev[role]?.includes(featureId)
        ? prev[role].filter(f => f !== featureId)
        : [...(prev[role] || []), featureId]
    }));
  };

  const handleSelectAll = (role) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: activeFeatures.map(f => f.id)
    }));
  };

  const handleDeselectAll = (role) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: []
    }));
  };

  const handleDeactivateRole = async (role) => {
    try {
      setSaving(true);

      // Check if any users have this role across all owner's dealerships
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_URL}/api/user-management/users-with-role?role=${encodeURIComponent(role)}`,
        { credentials: 'include', headers }
      );

      if (!response.ok) {
        throw new Error('Failed to check users with role');
      }

      const data = await response.json();
      const usersWithRole = data.users || [];

      if (usersWithRole.length > 0) {
        setConfirmModal({
          isOpen: true,
          title: 'Deactivate Role',
          message: `This role is assigned to ${usersWithRole.length} user(s):\n\n${usersWithRole.map(u => u.name).join('\n')}\n\nDeactivating will remove this role from all users. Continue?`,
          confirmStyle: 'danger',
          onConfirm: async () => {
            try {
              // Remove role from all users
              const removeResponse = await fetch(
                `${API_URL}/api/user-management/remove-role-from-users`,
                {
                  method: 'POST',
                  headers: { ...headers, 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ role })
                }
              );

              if (!removeResponse.ok) {
                throw new Error('Failed to remove role from users');
              }

              // Mark role as inactive
              const newInactiveRoles = [...inactiveRoles, role];
              setInactiveRoles(newInactiveRoles);

              setAlertModal({
                isOpen: true,
                title: 'Success',
                message: `Role "${role}" has been deactivated.`
              });
            } catch (err) {
              setAlertModal({
                isOpen: true,
                title: 'Error',
                message: err.message
              });
            } finally {
              setSaving(false);
            }
          }
        });
        return;
      }

      // Mark role as inactive (no users have this role)
      const newInactiveRoles = [...inactiveRoles, role];
      setInactiveRoles(newInactiveRoles);

      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: `Role "${role}" has been deactivated.`
      });
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: err.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReactivateRole = (role) => {
    const newInactiveRoles = inactiveRoles.filter(r => r !== role);
    setInactiveRoles(newInactiveRoles);
    setAlertModal({
      isOpen: true,
      title: 'Success',
      message: `Role "${role}" has been reactivated.`
    });
  };

  const handleAddCustomRole = () => {
    const name = customRoleInput.trim();
    if (!name) return;
    const existing = [...AVAILABLE_ROLES, ...customRoles].map(r => r.toLowerCase());
    if (existing.includes(name.toLowerCase())) {
      setAlertModal({ isOpen: true, title: 'Duplicate Role', message: `A role named "${name}" already exists.` });
      return;
    }
    setRolePermissions(prev => ({ ...prev, [name]: [] }));
    setCustomRoleInput('');
  };

  const handleDeleteCustomRole = async (role) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      setSaving(true);
      const res = await fetch(
        `${API_URL}/api/user-management/users-with-role?role=${encodeURIComponent(role)}`,
        { credentials: 'include', headers }
      );
      const data = await res.json();
      const usersWithRole = data.users || [];

      const doDelete = async () => {
        if (usersWithRole.length > 0) {
          await fetch(`${API_URL}/api/user-management/remove-role-from-users`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ role })
          });
        }
        const updated = { ...rolePermissions };
        delete updated[role];
        setRolePermissions(updated);
        const saveRes = await fetch(`${API_URL}/api/dealerships/role-permissions`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ rolePermissions: updated, ...(managedOwner ? { ownerId: managedOwner.id } : {}) })
        });
        if (!saveRes.ok) throw new Error('Failed to save');
        window.dispatchEvent(new CustomEvent('rolePermissionsUpdated'));
        setAlertModal({ isOpen: true, title: 'Role Deleted', message: `"${role}" has been removed.` });
      };

      const message = usersWithRole.length > 0
        ? `This role is assigned to ${usersWithRole.length} user(s):\n\n${usersWithRole.map(u => u.name).join('\n')}\n\nDeleting will remove this role from all users. Continue?`
        : `Are you sure you want to delete the "${role}" role? This cannot be undone.`;

      setConfirmModal({
        isOpen: true,
        title: 'Delete Custom Role',
        message,
        confirmStyle: 'danger',
        onConfirm: async () => {
          try { await doDelete(); } catch (e) { setAlertModal({ isOpen: true, title: 'Error', message: e.message }); } finally { setSaving(false); }
        }
      });
    } catch (e) {
      setAlertModal({ isOpen: true, title: 'Error', message: e.message });
      setSaving(false);
    }
  };

  const handleSave = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Save Role Permissions',
      message: 'Are you sure you want to save these global role permission changes? This will apply to all dealerships.',
      confirmStyle: 'primary',
      onConfirm: async () => {
        try {
          setSaving(true);
          const token = localStorage.getItem('token');
          const headers = { 'Content-Type': 'application/json' };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          console.log('[MANAGE ROLES] Saving global role permissions:', rolePermissions);
          console.log('[MANAGE ROLES] Owner permissions:', rolePermissions['Owner']);

          const response = await fetch(
            `${API_URL}/api/dealerships/role-permissions`,
            {
              method: 'PUT',
              headers,
              credentials: 'include',
              body: JSON.stringify({ rolePermissions, ...(managedOwner ? { ownerId: managedOwner.id } : {}) })
            }
          );

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to save role permissions');
          }

          // Trigger custom event so Dashboard re-fetches
          window.dispatchEvent(new CustomEvent('rolePermissionsUpdated'));
          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: 'Role permissions saved successfully!'
          });
          onClose();
        } catch (err) {
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: err.message
          });
        } finally {
          setSaving(false);
        }
      }
    });
  };

  // Simple loading state while fetching first time
  if (loading) {
    return (
      <div className="manage-roles-overlay" onClick={onClose}>
        <div className="manage-roles-modal" onClick={(e) => e.stopPropagation()}>
          <div className="manage-roles-header">
            <h2>Manage Role Permissions</h2>
            <button className="manage-roles-close" onClick={onClose}>×</button>
          </div>
          <div className="manage-roles-content">
            <LoadingState message="Loading role permissions..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-roles-overlay" onClick={onClose}>
      <div className="manage-roles-modal" onClick={(e) => e.stopPropagation()}>
        <div className="manage-roles-header">
          <h2>Manage Role Permissions</h2>
          <button className="manage-roles-close" onClick={onClose}>×</button>
        </div>

        <div className="manage-roles-content">
          <p className="manage-roles-description">
            Configure which features each role can access across all dealerships
          </p>

          <div className="roles-grid">
            {AVAILABLE_ROLES.map(role => {
              const isInactive = inactiveRoles.includes(role);
              return (
                <div key={role} className={`role-card ${isInactive ? 'inactive' : ''}`}>
                  <div className="role-card-header">
                    <div className="role-title-wrapper">
                      <h3>{role}</h3>
                      {isInactive && <span className="role-badge-inactive">Inactive</span>}
                    </div>
                    <div className="role-card-actions">
                      <button
                        className="role-action-btn select-all"
                        onClick={() => handleSelectAll(role)}
                        title="Select all features"
                        disabled={isInactive || saving}
                      >
                        All
                      </button>
                      <button
                        className="role-action-btn deselect-all"
                        onClick={() => handleDeselectAll(role)}
                        title="Deselect all features"
                        disabled={isInactive || saving}
                      >
                        None
                      </button>
                    </div>
                  </div>

                  <div className={`role-features ${isInactive ? 'disabled' : ''}`}>
                    {activeFeatures.map(feature => {
                      const isOwnerCriticalFeature = role === 'Owner' && ['user-management', 'dealerships'].includes(feature.id);
                      return (
                        <label key={feature.id} className="feature-checkbox" style={{ opacity: isInactive ? 0.5 : 1 }}>
                          <input
                            type="checkbox"
                            checked={isOwnerCriticalFeature || rolePermissions[role]?.includes(feature.id) || false}
                            onChange={() => handleToggleFeature(role, feature.id)}
                            disabled={isInactive || isOwnerCriticalFeature || saving}
                            title={isOwnerCriticalFeature ? 'This feature is always enabled for Owners' : ''}
                          />
                          <span>
                            {feature.label}
                            {feature.betaKey && <span style={{ marginLeft: '5px', fontSize: '10px', fontWeight: '700', color: 'rgba(139,92,246,0.9)', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '3px', padding: '0 4px', letterSpacing: '0.04em' }}>BETA</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {role === 'General Manager' && (
                    <div className="gm-management-features">
                      <div className="gm-management-label">Management permissions (this dealership only)</div>
                      {GM_MANAGEMENT_FEATURES.map(feature => (
                        <label key={feature.id} className="feature-checkbox" style={{ opacity: isInactive ? 0.5 : 1 }} title={feature.hint}>
                          <input
                            type="checkbox"
                            checked={rolePermissions[role]?.includes(feature.id) || false}
                            onChange={() => handleToggleFeature(role, feature.id)}
                            disabled={isInactive || saving}
                          />
                          <span>{feature.label}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  <div className="role-card-footer">
                    <span className="feature-count">
                      {rolePermissions[role]?.length || 0} / {activeFeatures.length} features
                    </span>
                    <button
                      className={isInactive ? 'role-reactivate-btn' : 'role-deactivate-btn'}
                      onClick={() => isInactive ? handleReactivateRole(role) : handleDeactivateRole(role)}
                      disabled={saving}
                      title={isInactive ? 'Reactivate this role' : 'Deactivate this role'}
                    >
                      {isInactive ? '↻ Reactivate' : '✕ Deactivate'}
                    </button>
                  </div>
                </div>
              );
            })}

            {customRoles.map(role => (
              <div key={role} className="role-card custom-role">
                <div className="role-card-header">
                  <div className="role-title-wrapper">
                    <h3>{role}</h3>
                    <span className="role-badge-custom">Custom</span>
                  </div>
                  <div className="role-card-actions">
                    <button className="role-action-btn select-all" onClick={() => handleSelectAll(role)} disabled={saving}>All</button>
                    <button className="role-action-btn deselect-all" onClick={() => handleDeselectAll(role)} disabled={saving}>None</button>
                  </div>
                </div>

                <div className="role-features">
                  {activeFeatures.map(feature => (
                    <label key={feature.id} className="feature-checkbox">
                      <input
                        type="checkbox"
                        checked={rolePermissions[role]?.includes(feature.id) || false}
                        onChange={() => handleToggleFeature(role, feature.id)}
                        disabled={saving}
                      />
                      <span>
                        {feature.label}
                        {feature.betaKey && <span style={{ marginLeft: '5px', fontSize: '10px', fontWeight: '700', color: 'rgba(139,92,246,0.9)', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '3px', padding: '0 4px', letterSpacing: '0.04em' }}>BETA</span>}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="role-card-footer">
                  <span className="feature-count">{rolePermissions[role]?.length || 0} / {activeFeatures.length} features</span>
                  <button
                    className="role-delete-btn"
                    onClick={() => handleDeleteCustomRole(role)}
                    disabled={saving}
                    title="Delete this custom role"
                  >
                    ✕ Delete Role
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="add-custom-role">
            <input
              type="text"
              className="custom-role-input"
              value={customRoleInput}
              onChange={e => setCustomRoleInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddCustomRole()}
              placeholder="New role name (e.g. Controller)"
              maxLength={50}
              disabled={saving}
            />
            <button
              className="add-role-btn"
              onClick={handleAddCustomRole}
              disabled={!customRoleInput.trim() || saving}
            >
              + Add Role
            </button>
          </div>
        </div>

        <div className="manage-roles-footer">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmStyle={confirmModal.confirmStyle}
      />

      {/* Alert Modal */}
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmText="OK"
        hideCancel={true}
        confirmStyle="primary"
      />
    </div>
  );
}
